import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import useWarranty from '../../hooks/useWarranty';
import useCustomer from '../../hooks/useCustomer';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import FormTextarea from '../../common/FormTextarea';
import FormDatePicker from '../../common/FormDatePicker';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import { formatDate } from '../../utils/formatters';
import { getCsrfToken } from '../../../utils/csrfUtils';
import './styles.css';

/**
 * 保養單表單組件
 * 用於創建或編輯保養單
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @param {string} props.customerId - 客戶ID (可選，用於創建特定客戶的保養單)
 * @param {string} props.warrantyId - 保養單ID (可選，用於編輯現有保養單)
 * @param {Function} props.onSuccess - 成功提交後的回調函數
 * @returns {JSX.Element} 保養單表單組件
 */
function WarrantyForm({ token, user, warrantyId, onSuccess }) {
    const navigate = useNavigate();
    const params = useParams();
    const urlCustomerId = params.customerId;

    // 使用自定義Hook
    const {
        warranties,
        loading: warrantyLoading,
        error: warrantyError,
        updateWarrantyStatus,
        updateLoading,
        updateError
    } = useWarranty(token);

    const {
        getCustomerById,
        customerLoading,
        customerError
    } = useCustomer(token);

    // 本地狀態
    const [formData, setFormData] = useState({
        productName: '',
        purchaseDate: '',
        receiptPhoto: null,
        notes: ''
    });

    const [customer, setCustomer] = useState(null);
    const [warranty, setWarranty] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(!!warrantyId);

    // 加載保養單數據（編輯模式）
    useEffect(() => {
        if (isEditMode && !warrantyLoading && warranties.length > 0) {
            const foundWarranty = warranties.find(w =>
                w.id === warrantyId ||
                w.id.split('/').pop() === warrantyId
            );

            if (foundWarranty) {
                setWarranty(foundWarranty);
                setFormData({
                    productName: foundWarranty.productName || '',
                    purchaseDate: foundWarranty.purchaseDate || '',
                    notes: foundWarranty.notes || ''
                });

                if (foundWarranty.receiptPhotoUrl) {
                    setPreviewUrl(foundWarranty.receiptPhotoUrl);
                }
            }
        }
    }, [isEditMode, warrantyId, warrantyLoading, warranties]);

    // 加載客戶數據
    useEffect(() => {
        if (urlCustomerId) {
            const loadCustomer = async () => {
                try {
                    // console.log('正在加載客戶數據，ID:', urlCustomerId);
                    const customerData = await getCustomerById(urlCustomerId);
                    // console.log('獲取到客戶數據:', customerData);
                    setCustomer(customerData);
                } catch (error) {
                    console.error('加載客戶數據時出錯:', error);
                    setSaveError('無法加載客戶數據，請確認客戶ID是否正確');
                }
            };

            loadCustomer();
        } else if (warranty && warranty.customerId) {
            const loadCustomer = async () => {
                try {
                    const customerData = await getCustomerById(warranty.customerId);
                    setCustomer(customerData);
                } catch (error) {
                    console.error('加載客戶數據時出錯:', error);
                }
            };

            loadCustomer();
        }
    }, [urlCustomerId, warranty, getCustomerById]);

    // 處理輸入變更
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 處理文件上傳
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                receiptPhoto: file
            }));

            // 創建預覽URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // 處理表單提交
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveError(null);

        try {
            // 驗證表單數據
            if (!formData.productName) {
                throw new Error('請輸入產品名稱');
            }

            if (!formData.purchaseDate) {
                throw new Error('請選擇購買日期');
            }

            if (!urlCustomerId && !isEditMode) {
                throw new Error('缺少客戶ID，無法創建保養單');
            }

            // 準備保養單數據
            const warrantyData = {
                productName: formData.productName,
                purchaseDate: formData.purchaseDate,
                notes: formData.notes
            };
            const csrfToken = getCsrfToken();
            // 如果有收據照片，先上傳
            if (formData.receiptPhoto) {
                const formDataForUpload = new FormData();
                formDataForUpload.append('file', formData.receiptPhoto);

                try {
                    // console.log('上傳收據照片...');
                    const uploadResponse = await fetch('/api/upload', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        body: formDataForUpload
                    });

                    if (!uploadResponse.ok) {
                        const errorData = await uploadResponse.json();
                        throw new Error(`上傳收據照片失敗: ${errorData.error || uploadResponse.statusText}`);
                    }

                    const uploadData = await uploadResponse.json();
                    // console.log('上傳響應:', uploadData);

                    // 優先使用 shopifyFileId，如果沒有則使用 url
                    if (uploadData.shopifyFileId) {
                        // console.log('使用 Shopify File ID:', uploadData.shopifyFileId);
                        warrantyData.receiptPhotoUrl = uploadData.shopifyFileId;
                    } else if (uploadData.url) {
                        // console.log('使用本地 URL:', uploadData.url);
                        warrantyData.receiptPhotoUrl = uploadData.url;
                    } else {
                        console.warn('上傳響應中沒有 shopifyFileId 或 url');
                    }
                } catch (uploadError) {
                    console.error('上傳收據照片時出錯:', uploadError);
                    throw new Error(`上傳收據照片失敗: ${uploadError.message}`);
                }
            }

            // 創建或更新保養單
            let response;
            const targetCustomerId = isEditMode ? (warranty?.customerId || urlCustomerId) : urlCustomerId;

            if (isEditMode) {
                // 更新現有保養單
                response = await fetch(`/api/customers/warranties/${warrantyId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-CSRF-Token': csrfToken
                    },
                    body: JSON.stringify(warrantyData)
                });
            } else {
                // 創建新保養單
                if (!targetCustomerId) {
                    throw new Error('無法確定客戶ID，請重新嘗試');
                }

                // console.log('創建保養單，客戶ID:', targetCustomerId);
                // console.log('保養單數據:', warrantyData);

                response = await fetch(`/api/customers/${targetCustomerId}/warranties`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-CSRF-Token': csrfToken
                    },
                    body: JSON.stringify(warrantyData)
                });
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`保存保養單失敗: ${errorData.error || response.statusText} (${response.status})`);
            }

            // 處理成功
            if (onSuccess) {
                onSuccess();
            } else {
                navigate(targetCustomerId ? `/customers/${targetCustomerId}/warranties` : '/warranties');
            }
        } catch (error) {
            console.error('保存保養單時出錯:', error);
            setSaveError(error.message || '保存保養單時出錯');
        } finally {
            setSaving(false);
        }
    };

    // 處理取消
    const handleCancel = () => {
        if (onSuccess) {
            onSuccess();
        } else {
            navigate(urlCustomerId ? `/customers/${urlCustomerId}/warranties` : '/warranties');
        }
    };

    // 加載中狀態
    if ((isEditMode && warrantyLoading) || customerLoading) {
        return <Loading text="正在加載數據..." />;
    }

    // 錯誤狀態
    if ((isEditMode && warrantyError) || customerError) {
        return (
            <ErrorMessage
                message={(isEditMode && warrantyError) || customerError}
                onRetry={() => window.location.reload()}
            />
        );
    }

    return (
        <div className="warranty-form-container">
            <Card
                title={isEditMode ? '編輯保養單' : '新增保養單'}
                className="warranty-form-card"
            >
                {customer && (
                    <div className="warranty-form-customer-info">
                        <h3>客戶信息</h3>
                        <p><strong>姓名:</strong> {customer.firstName} {customer.lastName}</p>
                        <p><strong>電子郵件:</strong> {customer.email}</p>
                        <p><strong>電話:</strong> {customer.phone}</p>
                    </div>
                )}

                {saveError && <ErrorMessage message={saveError} />}

                <form onSubmit={handleSubmit} className="warranty-form">
                    <FormInput
                        id="productName"
                        name="productName"
                        label="產品名稱"
                        value={formData.productName}
                        onChange={handleInputChange}
                        required
                        placeholder="請輸入產品名稱"
                    />

                    <FormDatePicker
                        id="purchaseDate"
                        name="purchaseDate"
                        label="購買日期"
                        value={formData.purchaseDate}
                        onChange={handleInputChange}
                        required
                    />

                    <div className="warranty-form-file-upload">
                        <label htmlFor="receiptPhoto">收據照片</label>
                        <input
                            type="file"
                            id="receiptPhoto"
                            name="receiptPhoto"
                            accept="image/*"
                            onChange={handleFileChange}
                        />

                        {previewUrl && (
                            <div className="warranty-form-preview">
                                <img src={previewUrl} alt="收據預覽" />
                            </div>
                        )}
                    </div>

                    <FormTextarea
                        id="notes"
                        name="notes"
                        label="備註"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="添加備註..."
                        rows={3}
                    />

                    <div className="warranty-form-actions">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                        >
                            取消
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            disabled={saving}
                        >
                            {saving ? '保存中...' : (isEditMode ? '更新保養單' : '創建保養單')}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}

export default WarrantyForm; 